import { useEffect, useRef, useState } from "react";
import "./style.scss";
import { AnimatedHeading, HoverText } from '../../shared/TextFx';
// TODO: point these at your actual images
import cargoImage from "../../assets/network-sec-img1.jpg";
import globeImage from "../../assets/network-sec-img2.jpg";

const TITLE = "Our Network";
const SUBTITLE = "One Control Tower. Global Reach.";
const SUB_HEADING = "One shipment. Coordinated from origin to destination.";
const DESCRIPTION =
  "From our 24/7 Control Tower in Chennai, Esquire coordinates Time Critical movements worldwide, working across carriers, customs, connections and established international networks to keep every shipment moving toward its required delivery.";

const icons = {
  office: (
     <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.5 15.5v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0v5l-8 5v2l8-2.5V18.5l-2 1.5V21.5l3.5-1 3.5 1V20l-2-1.5V13l8 2.5Z" />
    </svg>
  ),
  branch: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.5 15.5v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0v5l-8 5v2l8-2.5V18.5l-2 1.5V21.5l3.5-1 3.5 1V20l-2-1.5V13l8 2.5Z" />
    </svg>
  ),
  tower: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.5 15.5v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0v5l-8 5v2l8-2.5V18.5l-2 1.5V21.5l3.5-1 3.5 1V20l-2-1.5V13l8 2.5Z" />
    </svg>
  ),
  globe: (
     <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.5 15.5v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0v5l-8 5v2l8-2.5V18.5l-2 1.5V21.5l3.5-1 3.5 1V20l-2-1.5V13l8 2.5Z" />
    </svg>
  ),
};

// Order follows the design: card, card, image | image, card, card
const GRID = [
  { type: "card", icon: "office", value: "India", label: "Head Office" },
  { type: "card", icon: "branch", value: "Atlanta", label: "Branch Office" },
  {
    type: "image",
    src: cargoImage,
    alt: "Cargo aircraft landing beside palletised shipments on the runway",
  },
  {
    type: "image",
    src: globeImage,
    alt: "Night view of the globe with glowing connections between cities",
  },
  { type: "card", icon: "tower", value: "Chennai", label: "24/7 Control Tower" },
  { type: "card", icon: "globe", value: "Worldwide", label: "Partner Network" },
];

// Text that rolls up to a second copy on hover (the copy is aria-hidden)
const RollText = ({ text, className = "" }) => (
  <span className={`net-roll ${className}`}>
    <span className="net-roll__inner">
      <span className="net-roll__line">{text}</span>
      <span className="net-roll__line" aria-hidden="true">
        {text}
      </span>
    </span>
  </span>
);

const OurNetwork = ({ ctaHref = "/our-network" }) => {
  const introRef = useRef(null);
  const [jsReady, setJsReady] = useState(false);
  const [isIn, setIsIn] = useState(false);

  // Scroll-in animation: hidden state only applies once JS is running
  useEffect(() => {
    const el = introRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setIsIn(true);
      return undefined;
    }
    setJsReady(true);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIn(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const introClass = [
    "network-section__header",
    jsReady ? "js-anim" : "",
    isIn ? "is-in" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className="network-section">
      <div className="common-container-fluid">
        <header className={introClass} ref={introRef}>
          <div className="network-section__header-left">
            <AnimatedHeading className="h-display network-section__title" text={TITLE} />
          </div>

          <div className="network-section__header-right">
            <p className="net-subtitle net-anim" style={{ "--net-delay": "0s" }}>
              <span className="net-subtitle__text">{SUBTITLE}</span>
              <span className="net-subtitle-bar" aria-hidden="true" />
            </p>

            <h4 className="net-sub-heading net-anim" style={{ "--net-delay": "0.12s" }}>
              {SUB_HEADING}
            </h4>

            <p className="net-para-desc net-anim" style={{ "--net-delay": "0.24s" }}>
              {DESCRIPTION}
            </p>
          </div>
        </header>

        <div className="network-grid">
          {GRID.map((item, index) =>
            item.type === "card" ? (
              <div className="network-card scale-block" key={item.label}>
                <span className="network-card__icon">{icons[item.icon]}</span>
                <div>
                  <RollText text={item.value} className="network-card__value" />
                  <p className="network-card__label">
                    <span>{item.label}</span>
                  </p>
                </div>
              </div>
            ) : (
              <figure className="network-image" key={`image-${index}`}>
                <img
                  className="network-image__el"
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                />
              </figure>
            )
          )}
        </div>

        <div className="network-section__cta-wrap">
          <a href={ctaHref} className="btn _accent text-hover">
            <span className="btn__inner">
              <HoverText words="Explore Our Network" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default OurNetwork;
